const productBenefitsPlayers = new WeakMap();
const productBenefitsAutoPlayState = new WeakSet();

const createProductBenefitsPlayer = (section) => {
  const media = section.querySelector('.product-benefits-section__main-media');
  const player = section.querySelector('[data-product-benefits-segment-player]');
  const video = section.querySelector('[data-product-benefits-segment-video]');
  const defaultVideo = section.querySelector('[data-product-benefits-default-media] video');

  if (!media || !player || !(video instanceof HTMLVideoElement)) return null;

  let state = 'idle';
  let nextDirection = 'forward';
  let reverseRafId = null;
  let reverseStartTs = 0;
  let reverseFromTime = 0;
  const reverseSpeed = 1.1;
  let initialFramePrimed = false;
  const setActive = (isActive) => {
    media.classList.toggle('is-segment-active', isActive);
    media.dataset.productBenefitsSegmentActive = String(isActive);
    player.setAttribute('aria-hidden', String(!isActive));
  };

  const pauseDefaultVideo = () => {
    if (defaultVideo instanceof HTMLVideoElement) defaultVideo.pause();
  };

  const playDefaultVideo = () => {
    if (defaultVideo instanceof HTMLVideoElement) defaultVideo.play().catch(() => {});
  };

  const primeInitialFrame = () => {
    if (initialFramePrimed) return;

    const onLoadedData = () => {
      initialFramePrimed = true;
      video.removeEventListener('loadeddata', onLoadedData);

      if (state !== 'idle') return;

      try {
        video.currentTime = 0.01;
      } catch (_) {}

      video.pause();
    };

    video.addEventListener('loadeddata', onLoadedData, { once: true });
    video.load();
  };

  const cleanup = () => {
    video.removeEventListener('ended', onEnded);
    if (reverseRafId !== null) {
      cancelAnimationFrame(reverseRafId);
      reverseRafId = null;
    }
    reverseStartTs = 0;
    reverseFromTime = 0;
  };

  const onEnded = () => {
    state = 'ended';
    nextDirection = nextDirection === 'forward' ? 'reverse' : 'forward';
  };

  const finishReverse = () => {
    cleanup();
    video.pause();
    video.currentTime = 0;
    state = 'ended';
    nextDirection = 'forward';
  };

  const runReverse = (ts) => {
    if (state !== 'playing') return;
    if (reverseStartTs === 0) reverseStartTs = ts;

    // Time-based reverse is smoother than per-frame subtraction and avoids drift.
    const elapsed = (ts - reverseStartTs) / 1000;
    const targetTime = Math.max(0, reverseFromTime - elapsed * reverseSpeed);

    if (targetTime <= 0.01) {
      finishReverse();
      return;
    }

    // Avoid stacking seeks while the browser is still resolving previous one.
    if (!video.seeking) {
      video.currentTime = targetTime;
    }
    reverseRafId = requestAnimationFrame(runReverse);
  };

  const show = () => {
    cleanup();
    pauseDefaultVideo();
    setActive(true);
    video.pause();
    video.currentTime = 0;
    video.playbackRate = 1;
    state = 'idle';
    nextDirection = 'forward';
    primeInitialFrame();
  };

  const reset = () => {
    cleanup();
    pauseDefaultVideo();
    setActive(true);
    video.pause();
    video.currentTime = 0;
    video.playbackRate = 1;
    state = 'idle';
    nextDirection = 'forward';
    primeInitialFrame();
  };

  const hide = () => {
    cleanup();
    video.pause();
    video.currentTime = 0;
    video.playbackRate = 1;
    state = 'idle';
    nextDirection = 'forward';
    setActive(false);
    playDefaultVideo();
  };

  const play = () => {
    pauseDefaultVideo();
    setActive(true);

    if (state === 'playing') {
      return;
    }

    cleanup();

    if (nextDirection === 'forward') {
      video.pause();
      video.currentTime = 0;
      video.playbackRate = 1;
      video.addEventListener('ended', onEnded, { once: true });
      video.play().catch(() => {});
    } else {
      video.pause();
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        const onLoadedMeta = () => {
          video.removeEventListener('loadedmetadata', onLoadedMeta);
          if (state !== 'playing') return;
          reverseFromTime = video.duration || 0;
          video.currentTime = reverseFromTime;
          reverseRafId = requestAnimationFrame(runReverse);
        };
        video.addEventListener('loadedmetadata', onLoadedMeta);
        video.load();
      } else {
        reverseFromTime = video.duration;
        video.currentTime = reverseFromTime;
        reverseRafId = requestAnimationFrame(runReverse);
      }
      nextDirection = 'forward';
    }

    state = 'playing';
  };

  return { hide, show, reset, play };
};

const initProductBenefitsPlayer = (section) => {
  if (!(section instanceof Element)) return null;

  let player = productBenefitsPlayers.get(section);

  if (!player) {
    player = createProductBenefitsPlayer(section);
    if (player) {
      productBenefitsPlayers.set(section, player);

      if (section.dataset.productBenefitsStartOnBoard === 'true') {
        player.show();
        if (!productBenefitsAutoPlayState.has(section)) {
          productBenefitsAutoPlayState.add(section);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              player.play();
            });
          });
        }
      }
    }
  }

  return player;
};

const initProductBenefitsPlayers = (root = document) => {
  root.querySelectorAll('.product-benefits-section').forEach(initProductBenefitsPlayer);
};

let lastHandledPointerEvent = 0;

const handleProductBenefitsClick = (event) => {
  if (!(event.target instanceof Element)) return;
  if (event.target.closest('[data-sound-toggle]')) return;
  if (event.type === 'click' && Date.now() - lastHandledPointerEvent < 700) return;

  const trigger = event.target.closest('[data-product-benefits-game-trigger]');
  const playTrigger = event.target.closest('[data-product-benefits-play-trigger]');
  const watchTrigger = event.target.closest('[data-product-benefits-watch-trigger]');
  const clickedSegmentVideo = event.target.closest('[data-product-benefits-segment-video]');
  const clickedMedia = event.target.closest('.product-benefits-section__main-media');
  const clickedSegmentPlayer = event.target.closest('[data-product-benefits-segment-player]');

  if (!trigger && !clickedMedia && !clickedSegmentPlayer) return;

  if (event.cancelable) event.preventDefault();

  if (event.type.startsWith('pointer')) {
    lastHandledPointerEvent = Date.now();
  }

  const section = (trigger || clickedMedia || clickedSegmentPlayer).closest('.product-benefits-section');
  const media = section?.querySelector('.product-benefits-section__main-media');
  const segmentPlayerElement = section?.querySelector('[data-product-benefits-segment-player]');
  const playButton = section?.querySelector('[data-product-benefits-play-trigger]');
  const watchButton = section?.querySelector('[data-product-benefits-watch-trigger]');
  const playText = playButton?.dataset.playText || 'TAP TO PLAY';
  const watchText = watchButton?.dataset.watchText || 'TAP TO WATCH VIDEO';

  if (!section || !playButton || !watchButton || !segmentPlayerElement) return;

  const player = initProductBenefitsPlayer(section);
  const isSegmentActive = media?.dataset.productBenefitsSegmentActive === 'true';

  if (isSegmentActive) {
    if (clickedSegmentVideo) {
      player?.play();
      return;
    }

    if (watchTrigger) {
      player?.hide();
      playButton.hidden = false;
      watchButton.hidden = true;
      playButton.setAttribute('aria-expanded', 'false');
      watchButton.setAttribute('aria-expanded', 'false');
      playButton.setAttribute('aria-label', playText);
      watchButton.setAttribute('aria-label', watchText);
      return;
    }

    if (clickedSegmentPlayer) {
      player?.hide();
      playButton.hidden = false;
      watchButton.hidden = true;
      playButton.setAttribute('aria-expanded', 'false');
      watchButton.setAttribute('aria-expanded', 'false');
      playButton.setAttribute('aria-label', playText);
      watchButton.setAttribute('aria-label', watchText);
      return;
    }
  }

  if (!playTrigger && !clickedMedia) return;

  media?.classList.remove('is-game-active');
  playButton.hidden = true;
  watchButton.hidden = false;
  playButton.setAttribute('aria-expanded', 'true');
  watchButton.setAttribute('aria-expanded', 'true');
  playButton.setAttribute('aria-label', playText);
  watchButton.setAttribute('aria-label', watchText);

  player?.show();
};

document.addEventListener('pointerdown', handleProductBenefitsClick);
document.addEventListener('click', handleProductBenefitsClick);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductBenefitsPlayers(), { once: true });
} else {
  initProductBenefitsPlayers();
}

document.addEventListener('shopify:section:load', (event) => {
  initProductBenefitsPlayers(event.target);
});
